import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Routes, Route, useNavigate, useParams, Link } from 'react-router-dom'
import {
  ReactFlow, Background, Controls, MiniMap, addEdge, Connection, Edge, Node,
  useNodesState, useEdgesState, ReactFlowProvider, Panel
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import {
  Plus, Save, Play, Trash2, Download, Upload, ExternalLink, ArrowLeft, Calendar, User, HelpCircle
} from 'lucide-react'
import { toast } from 'sonner'

import { WorkshopNode } from './components/WorkshopNode'
import { KnotList } from './components/KnotList'
import {
  FullMap, MapNode, MapEdge, MapPulse, NODE_KINDS, NodeKind, Uncertainty, UNCERTAINTY_LABELS
} from './lib/types'

// --- API helpers (worker) ---
const API = '/api'

async function createMap(title: string, scenario?: string) {
  const res = await fetch(`${API}/maps`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title, scenario })
  })
  if (!res.ok) throw new Error('Failed to create map')
  return res.json() as Promise<{ id: string; edit_secret: string; title: string }>
}

async function fetchMap(mapId: string) {
  const res = await fetch(`${API}/maps/${mapId}`)
  if (!res.ok) throw new Error('Map not found')
  return res.json() as Promise<FullMap>
}

async function saveNodes(mapId: string, secret: string, nodes: Partial<MapNode>[]) {
  const res = await fetch(`${API}/maps/${mapId}/nodes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Edit-Secret': secret },
    body: JSON.stringify({ nodes })
  })
  if (!res.ok) throw new Error('Failed to save nodes')
  return res.json() as Promise<FullMap>
}

async function deleteNode(mapId: string, secret: string, nodeId: string) {
  const res = await fetch(`${API}/maps/${mapId}/nodes/${nodeId}`, {
    method: 'DELETE',
    headers: { 'X-Edit-Secret': secret }
  })
  if (!res.ok) throw new Error('Delete failed')
  return res.json() as Promise<FullMap>
}

async function addEdgeApi(mapId: string, secret: string, source: string, target: string, label?: string) {
  const res = await fetch(`${API}/maps/${mapId}/edges`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Edit-Secret': secret },
    body: JSON.stringify({ source_id: source, target_id: target, label })
  })
  if (!res.ok) throw new Error('Edge failed')
  return res.json() as Promise<FullMap>
}

async function castMap(mapId: string, secret: string) {
  const res = await fetch(`${API}/maps/${mapId}/cast`, {
    method: 'POST',
    headers: { 'X-Edit-Secret': secret }
  })
  return res.json()
}

async function scheduleJourney(mapId: string, secret: string, nodeId: string | null, note: string, days = 7) {
  const res = await fetch(`${API}/maps/${mapId}/journeys`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Edit-Secret': secret },
    body: JSON.stringify({ node_id: nodeId, note, due_in_days: days })
  })
  if (!res.ok) throw new Error('Journey schedule failed')
  return res.json() as Promise<FullMap>
}

async function getPulse(mapId: string) {
  const res = await fetch(`${API}/maps/${mapId}/pulse`)
  return res.json() as Promise<MapPulse>
}

async function requestIngest(mapId: string, text: string, secret?: string) {
  const res = await fetch(`${API}/ingest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ map_id: mapId, text, secret })
  })
  return res.json()
}

async function fetchProposals(mapId: string) {
  const res = await fetch(`${API}/maps/${mapId}/proposals`)
  return res.json()
}

async function acceptProposal(mapId: string, pid: string, secret: string) {
  const res = await fetch(`${API}/maps/${mapId}/proposals/${pid}/accept`, {
    method: 'POST',
    headers: { 'X-Edit-Secret': secret }
  })
  return res.json() as Promise<FullMap>
}

async function fullExport(mapId: string) {
  const res = await fetch(`${API}/maps/${mapId}/export`, { method: 'POST' })
  return res.json()
}

async function hardDelete(mapId: string, secret: string) {
  const res = await fetch(`${API}/maps/${mapId}`, {
    method: 'DELETE',
    headers: { 'X-Edit-Secret': secret }
  })
  return res.json()
}

// --- Demo seed (realistic nonprofit AI delegation confusion) ---
function createDemoMap(): { nodes: MapNode[]; edges: MapEdge[]; title: string; scenario: string } {
  const now = Date.now() - 1000 * 3600 * 5
  const nodes: MapNode[] = [
    {
      id: 'n1', map_id: 'demo', kind: 'delegation', label: 'AI drafted Q3 donor impact numbers',
      x: 180, y: 110, uncertainty: 'high',
      known: 'We gave the model the previous two quarters of program data and asked it to "project impact for the report".',
      unknown: 'We do not know exactly which rows were used vs hallucinated. No one re-ran the totals by hand.',
      evidence_need: 'A human-verified spreadsheet with source cells highlighted and methodology note attached.',
      next_question: 'Who will stand behind these numbers if a major donor or the board asks for the source calculation?',
      next_owner_hint: 'Director of Programs',
      created_at: now, updated_at: now
    },
    {
      id: 'n2', map_id: 'demo', kind: 'human_responsibility', label: 'Director of Programs (final sign-off)',
      x: 520, y: 80, uncertainty: 'manageable',
      known: 'She is the one who will present to the board.',
      unknown: 'She was not in the room when the AI prompt was written and has not seen the raw model output.',
      evidence_need: '30-minute review meeting with the raw data file before the report is sent.',
      next_question: 'Can she block publication if the evidence trail is weak?',
      next_owner_hint: 'Director of Programs',
      created_at: now, updated_at: now
    },
    {
      id: 'n3', map_id: 'demo', kind: 'assumption', label: '“The model only used the numbers we gave it”',
      x: 180, y: 310, uncertainty: 'critical',
      known: 'Prompt said “use only the attached CSV”.',
      unknown: 'No one checked the actual token context or whether the model pulled from training data.',
      evidence_need: 'Full prompt + attached file hash + a sample of the model’s chain-of-thought if available.',
      next_question: 'Did the model cite any external statistics we did not provide?',
      next_owner_hint: 'Ops analyst who ran the prompt',
      created_at: now, updated_at: now
    },
    {
      id: 'n4', map_id: 'demo', kind: 'evidence', label: 'Q2 actuals spreadsheet (source of truth)',
      x: 520, y: 260, uncertainty: 'negligible',
      known: 'This file was exported directly from Salesforce on July 3.',
      unknown: null,
      evidence_need: null,
      next_question: null,
      next_owner_hint: null,
      created_at: now, updated_at: now
    },
    {
      id: 'n5', map_id: 'demo', kind: 'open_question', label: 'Who owns the narrative that will accompany these numbers?',
      x: 820, y: 170, uncertainty: 'high',
      known: 'The Communications team usually writes the story.',
      unknown: 'Comms was not told that some of the underlying figures came from an unverified AI pass.',
      evidence_need: 'Written confirmation from Comms + Programs that they have read the uncertainty log.',
      next_question: 'If a donor challenges a specific claim, who fields the question and on what authority?',
      next_owner_hint: 'Comms Director + Director of Programs',
      created_at: now, updated_at: now
    },
    {
      id: 'n6', map_id: 'demo', kind: 'system_constraint', label: 'Funder requires “data-backed” claims in all public reports',
      x: 820, y: 340, uncertainty: 'manageable',
      known: 'Clause 4.2 of the grant agreement.',
      unknown: 'We do not have written guidance on whether model-assisted calculations count as data-backed.',
      evidence_need: 'Email from program officer clarifying the standard (or a risk note in our files).',
      next_question: 'Should we proactively disclose the use of AI assistance in methodology footnotes?',
      next_owner_hint: 'Grants Manager',
      created_at: now, updated_at: now
    }
  ]
  const edges: MapEdge[] = [
    { id: 'e1', map_id: 'demo', source_id: 'n1', target_id: 'n2', label: 'is owned by', created_at: now },
    { id: 'e2', map_id: 'demo', source_id: 'n1', target_id: 'n3', label: 'rests on', created_at: now },
    { id: 'e3', map_id: 'demo', source_id: 'n3', target_id: 'n4', label: 'should be verified against', created_at: now },
    { id: 'e4', map_id: 'demo', source_id: 'n2', target_id: 'n5', label: 'must approve narrative from', created_at: now },
    { id: 'e5', map_id: 'demo', source_id: 'n5', target_id: 'n6', label: 'must satisfy', created_at: now },
  ]
  return {
    nodes,
    edges,
    title: 'Q3 Donor Impact Report — AI numbers confusion',
    scenario: 'We used an LLM to accelerate the impact section of a major funder report. Now several people on the team are uneasy about the provenance of specific figures. Board meeting in 11 days.'
  }
}

// --- Main Workshop Component ---
function Workshop() {
  const { mapId } = useParams()
  const navigate = useNavigate()

  const [fullMap, setFullMap] = useState<FullMap | null>(null)
  const [editSecret, setEditSecret] = useState<string>('')
  const [pulse, setPulse] = useState<MapPulse | null>(null)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [showIngest, setShowIngest] = useState(false)
  const [ingestText, setIngestText] = useState('')
  const [showJourneys, setShowJourneys] = useState(false)
  const [proposals, setProposals] = useState<any[]>([])

  // React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])

  const nodeTypes = useMemo(() => ({ workshop: WorkshopNode }), [])

  const selectedNode = useMemo(() => {
    return fullMap?.nodes.find(n => n.id === selectedNodeId) || null
  }, [fullMap, selectedNodeId])

  // Load map
  const loadMap = useCallback(async (id: string, secretFromUrl?: string) => {
    try {
      const data = await fetchMap(id)
      setFullMap(data)
      if (secretFromUrl) setEditSecret(secretFromUrl)

      // Seed React Flow
      const flowNodes: Node[] = data.nodes.map(n => ({
        id: n.id,
        type: 'workshop',
        position: { x: n.x, y: n.y },
        data: { ...n },
        selected: n.id === selectedNodeId,
      }))
      const flowEdges: Edge[] = data.edges.map(e => ({
        id: e.id,
        source: e.source_id,
        target: e.target_id,
        label: e.label || '',
        type: 'smoothstep',
        animated: false,
      }))
      setNodes(flowNodes)
      setEdges(flowEdges)

      // Load pulse + proposals
      const p = await getPulse(id)
      setPulse(p)
      const props = await fetchProposals(id)
      setProposals(props)
    } catch (e) {
      toast.error('Could not load map. Check the link or create a new one.')
      navigate('/')
    }
  }, [navigate, selectedNodeId, setNodes, setEdges])

  useEffect(() => {
    if (!mapId) return

    // Support secret in hash for reloads: #secret=xxxx
    const hash = window.location.hash
    const secretMatch = hash.match(/secret=([A-Za-z0-9]+)/)
    const secret = secretMatch ? secretMatch[1] : (editSecret || '')

    loadMap(mapId, secret)
  }, [mapId, loadMap])

  // Autosave positions + data when nodes change significantly
  const debouncedSave = useCallback(async (changedNodes: any[]) => {
    if (!mapId || !editSecret || !fullMap) return
    setIsSaving(true)
    try {
      const payload = changedNodes.map(n => ({
        id: n.id,
        kind: n.data.kind,
        label: n.data.label,
        x: Math.round(n.position.x),
        y: Math.round(n.position.y),
        uncertainty: n.data.uncertainty,
        known: n.data.known,
        unknown: n.data.unknown,
        evidence_need: n.data.evidence_need,
        next_question: n.data.next_question,
        next_owner_hint: n.data.next_owner_hint,
      }))
      const updated = await saveNodes(mapId, editSecret, payload)
      setFullMap(updated)
    } catch (e) {
      // silent; user will see manual save button
    } finally {
      setIsSaving(false)
    }
  }, [mapId, editSecret, fullMap])

  // React Flow node drag / change → persist positions
  useEffect(() => {
    const changed = nodes.filter(n => {
      const orig = fullMap?.nodes.find(o => o.id === n.id)
      return orig && (Math.abs(orig.x - n.position.x) > 3 || Math.abs(orig.y - n.position.y) > 3)
    })
    if (changed.length > 0) {
      const t = setTimeout(() => debouncedSave(changed), 650)
      return () => clearTimeout(t)
    }
  }, [nodes, debouncedSave, fullMap])

  // When user clicks a node in flow
  const onNodeClick = useCallback((_: any, node: Node) => {
    setSelectedNodeId(node.id)
  }, [])

  const onPaneClick = useCallback(() => setSelectedNodeId(null), [])

  // Add new node (toolbar)
  const addNode = async (kind: NodeKind) => {
    if (!mapId || !editSecret) {
      toast.error('Create or load a map first')
      return
    }
    const newNode: Partial<MapNode> = {
      id: 'node_' + Math.random().toString(36).slice(2, 11),
      kind,
      label: NODE_KINDS.find(k => k.value === kind)?.label || 'New knot',
      x: 260 + (nodes.length % 5) * 70,
      y: 140 + Math.floor(nodes.length / 3) * 90,
      uncertainty: null,
      known: null,
      unknown: null,
      evidence_need: null,
      next_question: null,
      next_owner_hint: null,
    }
    try {
      const updated = await saveNodes(mapId, editSecret, [newNode])
      setFullMap(updated)
      setNodes(updated.nodes.map(n => ({
        id: n.id, type: 'workshop', position: { x: n.x, y: n.y }, data: { ...n }
      })))
      setSelectedNodeId(newNode.id!)
      toast.success('New knot added. Fill the four fields on the right.')
    } catch (e) {
      toast.error('Could not add node')
    }
  }

  // Update a single field on the selected node (the four capture fields live here)
  const updateSelectedField = async (field: keyof MapNode, value: string | null) => {
    if (!selectedNode || !mapId || !editSecret || !fullMap) return

    const updatedNodes = fullMap.nodes.map(n =>
      n.id === selectedNode.id ? { ...n, [field]: value } : n
    )
    setFullMap({ ...fullMap, nodes: updatedNodes })

    // Also update flow node data immediately for visual feedback
    setNodes(nds => nds.map(nd =>
      nd.id === selectedNode.id
        ? { ...nd, data: { ...nd.data, [field]: value } }
        : nd
    ))

    // Persist
    try {
      await saveNodes(mapId, editSecret, [{
        id: selectedNode.id,
        [field]: value,
        kind: selectedNode.kind,
        label: selectedNode.label,
        x: selectedNode.x,
        y: selectedNode.y,
      } as any])
    } catch (e) {
      toast.error('Autosave failed — use Save button')
    }
  }

  const updateLabel = async (newLabel: string) => {
    if (!selectedNode) return
    await updateSelectedField('label' as any, newLabel)
  }

  // Connect nodes (responsibility wiring)
  const onConnect = useCallback(async (connection: Connection) => {
    if (!mapId || !editSecret || !connection.source || !connection.target) return
    try {
      const updated = await addEdgeApi(mapId, editSecret, connection.source, connection.target, 'related')
      setFullMap(updated)
      setEdges(updated.edges.map(e => ({
        id: e.id, source: e.source_id, target: e.target_id, label: e.label || '', type: 'smoothstep'
      })))
    } catch (e) {
      toast.error('Could not connect')
    }
  }, [mapId, editSecret, setEdges])

  // Delete node
  const removeSelected = async () => {
    if (!selectedNodeId || !mapId || !editSecret) return
    if (!confirm('Remove this knot from the map?')) return
    try {
      const updated = await deleteNode(mapId, editSecret, selectedNodeId)
      setFullMap(updated)
      setNodes(updated.nodes.map(n => ({ id: n.id, type: 'workshop', position: { x: n.x, y: n.y }, data: { ...n } })))
      setEdges(updated.edges.map(e => ({ id: e.id, source: e.source_id, target: e.target_id, label: e.label || '' })))
      setSelectedNodeId(null)
    } catch (e) { toast.error('Delete failed') }
  }

  // Cast → produce the Responsibility Map artifact
  const cast = async () => {
    if (!mapId || !editSecret) return
    try {
      await castMap(mapId, editSecret)
      toast.success('Responsibility Map cast. You can now share the clean view.', { duration: 6000 })
      navigate(`/map/${mapId}`)
    } catch (e) {
      toast.error('Cast failed')
    }
  }

  // Ingest confusing thread
  const submitIngest = async () => {
    if (!mapId || !ingestText.trim()) return
    try {
      await requestIngest(mapId, ingestText, editSecret)
      setIngestText('')
      setShowIngest(false)
      toast.success('Ingestion queued. Proposals will appear below the canvas shortly.', { duration: 5000 })
      // poll proposals after a short delay
      setTimeout(async () => {
        const p = await fetchProposals(mapId)
        setProposals(p)
      }, 1400)
    } catch (e) {
      toast.error('Ingest request failed')
    }
  }

  const acceptProp = async (pid: string) => {
    if (!mapId || !editSecret) return
    const updated = await acceptProposal(mapId, pid, editSecret)
    setFullMap(updated)
    setNodes(updated.nodes.map(n => ({ id: n.id, type: 'workshop', position: { x: n.x, y: n.y }, data: { ...n } })))
    const p = await fetchProposals(mapId)
    setProposals(p)
    toast.success('Proposal accepted and turned into a knot on the map')
  }

  // Schedule a journey from a node
  const scheduleRevisit = async () => {
    if (!selectedNode || !mapId || !editSecret) return
    const note = prompt('What should we check when we revisit?', selectedNode.evidence_need || 'Update evidence status and ownership clarity')
    if (!note) return
    const days = Number(prompt('In how many days?', '7')) || 7
    try {
      const updated = await scheduleJourney(mapId, editSecret, selectedNode.id, note, days)
      setFullMap(updated)
      toast.success(`Journey scheduled. The workflow will surface a reminder in ~${days} days (demo: minutes).`)
    } catch (e) { toast.error('Could not schedule') }
  }

  // Export full portable artifact
  const doExport = async () => {
    if (!mapId) return
    const data = await fullExport(mapId)
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `responsibility-map-${mapId}.json`
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Full export downloaded. This is your complete audit artifact.')
  }

  // Delete entire map
  const doDelete = async () => {
    if (!mapId || !editSecret) return
    if (!confirm('Permanently delete this entire map and all its repair work? This cannot be undone.')) return
    await hardDelete(mapId, editSecret)
    toast('Map permanently deleted')
    navigate('/')
  }

  // Load realistic demo (creates real map on the backend)
  const loadDemo = async () => {
    const demo = createDemoMap()
    try {
      const created = await createMap(demo.title, demo.scenario)
      setEditSecret(created.edit_secret)
      // Save the seeded nodes
      await saveNodes(created.id, created.edit_secret, demo.nodes)
      // Save edges
      for (const e of demo.edges) {
        await addEdgeApi(created.id, created.edit_secret, e.source_id, e.target_id, e.label || undefined)
      }
      // Navigate into it (secret in hash so reload works)
      window.location.href = `/workshop/${created.id}#secret=${created.edit_secret}`
    } catch (e) {
      toast.error('Demo seed failed — is the worker running?')
    }
  }

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'n' && !e.target || (e.target as HTMLElement).tagName === 'BODY') {
        addNode('delegation')
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        if (mapId && editSecret) toast.success('Map is autosaved continuously. Use Cast for the final artifact.')
      }
      if (e.key === 'Escape') setSelectedNodeId(null)
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedNodeId) removeSelected()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedNodeId, mapId, editSecret])

  const canEdit = !!editSecret

  if (!fullMap) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f7f4]">
        <div className="text-center">
          <div className="text-sm tracking-[3px] text-bench-accent mb-2">THE BENCH</div>
          <div className="text-2xl">Loading workshop…</div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#f8f7f4] text-bench-ink flex flex-col">
      {/* Calm but urgent header */}
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur sticky top-0 z-50 no-print">
        <div className="max-w-[1280px] mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-semibold tracking-tight flex items-center gap-2">
              <span className="text-bench-accent">●</span> Delegation Bench
            </Link>
            <div className="text-xs px-2 py-px rounded bg-amber-100 text-amber-800">REPAIR WORKSHOP</div>
          </div>

          <div className="flex items-center gap-3 text-sm">
            <div className="text-slate-500 hidden md:block">
              {fullMap.map.title} {isSaving && '· saving…'}
            </div>
            <button onClick={() => navigate(`/map/${mapId}`)} className="flex items-center gap-1.5 px-3 py-1.5 rounded border hover:bg-white">
              <ExternalLink size={15} /> Cast View
            </button>
            <button onClick={cast} disabled={!canEdit} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-bench-accent text-white disabled:opacity-50">
              <Play size={15} /> Cast Responsibility Map
            </button>
            <button onClick={doDelete} className="text-red-600 flex items-center gap-1 px-2 py-1 text-xs">DELETE</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1280px] mx-auto w-full px-5 pt-4 pb-8 flex-1">
        {/* Top bar: scenario + pulse + actions */}
        <div className="flex flex-wrap gap-x-6 gap-y-3 items-end mb-3">
          <div className="flex-1 min-w-[280px]">
            <input
              value={fullMap.map.title}
              onChange={async (e) => {
                const title = e.target.value
                setFullMap(f => f ? { ...f, map: { ...f.map, title } } : f)
                if (mapId && editSecret) {
                  await fetch(`${API}/maps/${mapId}`, {
                    method: 'PATCH', headers: { 'Content-Type': 'application/json', 'X-Edit-Secret': editSecret },
                    body: JSON.stringify({ title })
                  })
                }
              }}
              className="text-2xl font-semibold bg-transparent border-b border-transparent focus:border-bench-accent w-full outline-none"
            />
            <textarea
              value={fullMap.map.scenario || ''}
              onChange={async (e) => {
                const scenario = e.target.value
                setFullMap(f => f ? { ...f, map: { ...f.map, scenario } } : f)
                if (mapId && editSecret) {
                  await fetch(`${API}/maps/${mapId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', 'X-Edit-Secret': editSecret }, body: JSON.stringify({ scenario }) })
                }
              }}
              placeholder="One-sentence context for this delegation confusion (optional but valuable for future you and your team)"
              className="w-full text-[13px] mt-1 text-slate-600 bg-transparent resize-y min-h-[42px] outline-none"
            />
          </div>

          <div className="flex gap-2">
            <button onClick={() => setShowIngest(true)} className="flex items-center gap-2 px-3 py-2 border rounded text-sm hover:bg-white">
              <Upload size={15} /> Ingest confusing thread
            </button>
            <button onClick={loadDemo} className="flex items-center gap-2 px-3 py-2 border rounded text-sm hover:bg-white">
              Load realistic nonprofit example
            </button>
            <button onClick={doExport} className="flex items-center gap-2 px-3 py-2 border rounded text-sm hover:bg-white">
              <Download size={15} /> Export full JSON
            </button>
          </div>
        </div>

        {/* Live Pulse (the only "analytics" — entirely for the user making the map) */}
        {pulse && (
          <div className="mb-4 text-xs flex flex-wrap gap-x-5 gap-y-1 text-slate-600 bg-white border rounded px-4 py-2">
            <span><span className="font-medium text-bench-ink">{pulse.total_nodes}</span> knots mapped</span>
            <span><span className="font-medium text-bench-ink">{pulse.four_fields_complete}</span> with all four repair fields</span>
            <span><span className="font-medium text-bench-ink">{pulse.completion_pct}%</span> field completion</span>
            <span className="text-bench-knot">{pulse.critical_knots} critical + {pulse.high_knots} high uncertainty</span>
            <span>{pulse.ai_delegations} explicit AI delegations</span>
            <span>{pulse.open_journeys} open journeys</span>
          </div>
        )}

        <div className="flex gap-4">
          {/* Canvas */}
          <div className="flex-1">
            <div className="workshop-canvas border border-slate-200 rounded-xl overflow-hidden h-[620px] bg-[#f8f7f4] relative">
              <ReactFlowProvider>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  onNodeClick={onNodeClick}
                  onPaneClick={onPaneClick}
                  nodeTypes={nodeTypes}
                  fitView
                  minZoom={0.4}
                  maxZoom={1.6}
                  proOptions={{ hideAttribution: true }}
                >
                  <Background color="#cbd5e1" gap={24} />
                  <Controls />
                  <MiniMap nodeStrokeWidth={2} />

                  {/* Toolbar */}
                  <Panel position="top-left" className="bg-white/95 border rounded-lg shadow-sm p-1.5 m-3 flex flex-wrap gap-1 text-xs">
                    {NODE_KINDS.map(k => (
                      <button key={k.value} onClick={() => addNode(k.value)} className="px-2.5 py-1 rounded hover:bg-amber-50 border border-transparent hover:border-amber-200 flex items-center gap-1.5">
                        <Plus size={13} /> {k.label}
                      </button>
                    ))}
                    <div className="w-px h-5 bg-slate-200 mx-1 self-center" />
                    <button onClick={removeSelected} disabled={!selectedNodeId} className="px-2.5 py-1 rounded text-red-600 disabled:opacity-40 flex items-center gap-1">
                      <Trash2 size={13} /> Remove selected
                    </button>
                  </Panel>

                  <Panel position="bottom-center" className="mb-3 text-[11px] text-slate-500 bg-white/80 px-3 py-px rounded">
                    Drag knots • Click to repair • Connect responsibility with edges • Press N to add delegation knot • Esc to deselect
                  </Panel>
                </ReactFlow>
              </ReactFlowProvider>
            </div>

            {/* Proposals from ingest */}
            {proposals.length > 0 && (
              <div className="mt-3 border rounded-lg bg-white p-3">
                <div className="uppercase text-xs tracking-widest text-bench-accent mb-2">Ingest proposals — review and accept into the map</div>
                <div className="space-y-2">
                  {proposals.map((p: any) => (
                    <div key={p.id} className="border rounded p-2 text-sm flex justify-between gap-3">
                      <div>
                        <div className="font-medium">{p.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{p.source_excerpt}</div>
                        <div className="text-[11px] mt-1 text-amber-700">Next question suggested: {p.next_question}</div>
                      </div>
                      <div className="flex flex-col gap-1 text-xs shrink-0">
                        <button onClick={() => acceptProp(p.id)} className="px-3 py-1 bg-emerald-700 text-white rounded">Accept as knot</button>
                        <button onClick={() => { /* dismiss is quiet */ }} className="text-slate-400 hover:text-slate-600">dismiss</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right inspector — the actual "repair" work happens here */}
          <div className="w-[340px] shrink-0 border bg-white rounded-xl p-4 flex flex-col">
            {!selectedNode ? (
              <div className="flex-1 flex flex-col">
                <div className="font-semibold mb-2">Open knots needing repair</div>
                <KnotList nodes={fullMap.nodes} onFocus={setSelectedNodeId} selectedId={selectedNodeId} />

                <div className="mt-auto pt-4 border-t text-xs text-slate-500 space-y-2">
                  <div className="flex items-center gap-2"><HelpCircle size={14} /> Select any knot on the canvas to begin repair work.</div>
                  <div>Focus on the four fields on the right when a knot is selected. These become the heart of the final Responsibility Map.</div>
                  <button onClick={() => setShowJourneys(!showJourneys)} className="mt-1 text-bench-accent flex items-center gap-1">
                    <Calendar size={14} /> {showJourneys ? 'Hide' : 'Show'} active follow-up journeys
                  </button>
                  {showJourneys && (
                    <div className="text-xs bg-slate-50 p-2 rounded">
                      {fullMap.journeys.length === 0 && 'No journeys scheduled yet. Select a knot → Schedule revisit.'}
                      {fullMap.journeys.map(j => (
                        <div key={j.id} className="py-1 border-b last:border-0">Due {new Date(j.due_at).toLocaleDateString()} — {j.note}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-auto">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="uppercase tracking-widest text-xs text-bench-accent">REPAIR CARD</div>
                    <div className="text-xs text-slate-500">All changes save automatically</div>
                  </div>
                  <button onClick={() => setSelectedNodeId(null)} className="text-xs text-slate-400">close</button>
                </div>

                {/* Label */}
                <input
                  value={selectedNode.label}
                  onChange={e => updateLabel(e.target.value)}
                  className="w-full text-lg font-medium border-b pb-1 mb-4 outline-none"
                />

                {/* Uncertainty label — first of the four required signals */}
                <div className="mb-4">
                  <div className="field-label mb-1">Uncertainty label</div>
                  <div className="flex flex-wrap gap-1.5">
                    {UNCERTAINTY_LABELS.map(u => (
                      <button
                        key={u}
                        onClick={() => updateSelectedField('uncertainty' as any, u)}
                        className={`px-3 py-0.5 text-xs rounded-full border ${selectedNode.uncertainty === u ? 'border-bench-accent bg-amber-50' : 'border-slate-200 hover:bg-slate-50'}`}
                      >
                        {u}
                      </button>
                    ))}
                    <button onClick={() => updateSelectedField('uncertainty' as any, null)} className="text-xs text-slate-400 px-1">clear</button>
                  </div>
                </div>

                {/* Known / Unknown split — the cognitive core */}
                <div className="mb-4 grid grid-cols-1 gap-3">
                  <div>
                    <div className="field-label mb-1 flex items-center gap-1">Known for certain <span className="text-emerald-600">●</span></div>
                    <textarea
                      value={selectedNode.known || ''}
                      onChange={e => updateSelectedField('known' as any, e.target.value || null)}
                      placeholder="What is verifiably true right now?"
                      className="probe-input w-full h-20 text-sm p-2 rounded border resize-y"
                    />
                  </div>
                  <div>
                    <div className="field-label mb-1 flex items-center gap-1">Unknown / assumed / risky <span className="text-amber-600">●</span></div>
                    <textarea
                      value={selectedNode.unknown || ''}
                      onChange={e => updateSelectedField('unknown' as any, e.target.value || null)}
                      placeholder="What we are guessing, what could be wrong, what is missing"
                      className="probe-input w-full h-20 text-sm p-2 rounded border resize-y"
                    />
                  </div>
                </div>

                {/* Evidence need */}
                <div className="mb-4">
                  <div className="field-label mb-1">Evidence need — what would close or reduce this?</div>
                  <textarea
                    value={selectedNode.evidence_need || ''}
                    onChange={e => updateSelectedField('evidence_need' as any, e.target.value || null)}
                    placeholder="A specific document, conversation, re-calculation, or verification step"
                    className="probe-input w-full h-16 text-sm p-2 rounded border resize-y"
                  />
                </div>

                {/* Next question + owner hint */}
                <div className="mb-4">
                  <div className="field-label mb-1">Next question (the sharpest one)</div>
                  <textarea
                    value={selectedNode.next_question || ''}
                    onChange={e => updateSelectedField('next_question' as any, e.target.value || null)}
                    placeholder="The single most useful question to ask next to reduce the uncertainty"
                    className="probe-input w-full h-16 text-sm p-2 rounded border resize-y"
                  />
                  <div className="mt-2">
                    <div className="field-label mb-1 text-xs">Best asked of / owned by</div>
                    <input
                      value={selectedNode.next_owner_hint || ''}
                      onChange={e => updateSelectedField('next_owner_hint' as any, e.target.value || null)}
                      placeholder="Role, person, or data source"
                      className="probe-input w-full text-sm p-2 rounded border"
                    />
                  </div>
                </div>

                <div className="flex gap-2 pt-2 border-t">
                  <button onClick={scheduleRevisit} className="flex-1 flex items-center justify-center gap-2 text-sm py-2 border rounded hover:bg-slate-50">
                    <Calendar size={15} /> Schedule revisit
                  </button>
                  <button onClick={removeSelected} className="text-red-600 px-3 border rounded hover:bg-red-50">Remove</button>
                </div>

                <div className="text-[10px] text-slate-400 mt-4">
                  These four fields (uncertainty label + known/unknown + evidence need + next question) are what turn a vague worry into a repairable responsibility map.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ingest modal */}
      {showIngest && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70] p-4" onClick={() => setShowIngest(false)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-5" onClick={e => e.stopPropagation()}>
            <div className="font-semibold mb-1">Ingest confusing thread or document excerpt</div>
            <p className="text-sm text-slate-600 mb-3">Paste emails, AI chat logs, meeting notes, or a report section. The system will propose starter knots that you can accept or ignore. Nothing is auto-added to your map.</p>
            <textarea value={ingestText} onChange={e => setIngestText(e.target.value)} className="w-full h-40 border p-3 text-sm rounded" placeholder="Paste here..." />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setShowIngest(false)} className="px-4 py-1.5">Cancel</button>
              <button onClick={submitIngest} className="px-5 py-1.5 bg-bench-accent text-white rounded">Queue for extraction</button>
            </div>
            <div className="text-xs text-slate-400 mt-3">Your pasted text is only used to generate proposals for this map. It is not stored or used for any other purpose.</div>
          </div>
        </div>
      )}

      {/* Footer creator credit — prominent as requested */}
      <footer className="border-t py-5 text-xs text-slate-500 bg-white mt-auto no-print">
        <div className="max-w-[1280px] mx-auto px-5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
          <div>
            Delegation Bench is a public-utility cognitive instrument. Your clarity is the product. No data extraction beyond what you explicitly place on the map.
          </div>
          <div className="flex gap-x-4">
            <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noreferrer" className="hover:text-bench-accent flex items-center gap-1">
              <User size={13} /> Abhay Pratap Singh
            </a>
            <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noreferrer" className="hover:text-bench-accent">Buy me a coffee</a>
          </div>
        </div>
      </footer>
    </div>
  )
}

// --- Cast / Responsibility Map view (the deliverable artifact) ---
function CastView() {
  const { mapId } = useParams()
  const [full, setFull] = useState<FullMap | null>(null)

  useEffect(() => {
    if (mapId) fetchMap(mapId).then(setFull).catch(() => {})
  }, [mapId])

  if (!full) return <div className="p-10 text-center">Loading cast map…</div>

  const high = full.nodes.filter(n => n.uncertainty === 'high' || n.uncertainty === 'critical')
  const complete = full.nodes.filter(n => n.known && n.unknown && n.evidence_need && n.next_question)

  const handlePrint = () => window.print()

  return (
    <div className="min-h-screen bg-white responsibility-map">
      <div className="max-w-[860px] mx-auto px-6 py-10">
        <div className="flex justify-between items-start mb-8 no-print">
          <div>
            <div className="uppercase tracking-[2px] text-xs text-bench-accent">RESPONSIBILITY MAP</div>
            <h1 className="text-3xl font-semibold mt-1">{full.map.title}</h1>
            {full.map.scenario && <p className="mt-2 text-slate-600 max-w-prose">{full.map.scenario}</p>}
          </div>
          <div className="text-right text-xs">
            Cast {full.map.cast_at ? new Date(full.map.cast_at).toLocaleDateString() : 'now'}<br />
            {full.nodes.length} knots • {high.length} high/critical uncertainty
          </div>
        </div>

        {/* Summary stats for the artifact */}
        <div className="grid grid-cols-3 gap-3 mb-8 text-sm">
          <div className="border p-3 rounded">
            <div className="text-xs text-slate-500">AI DELEGATIONS WITH OPEN UNCERTAINTY</div>
            <div className="text-2xl mt-1 font-medium">{high.length}</div>
          </div>
          <div className="border p-3 rounded">
            <div className="text-xs text-slate-500">KNOTS WITH FULL KNOWN/UNKNOWN + EVIDENCE + NEXT QUESTION</div>
            <div className="text-2xl mt-1 font-medium">{complete.length}</div>
          </div>
          <div className="border p-3 rounded">
            <div className="text-xs text-slate-500">ACTIVE FOLLOW-UP JOURNEYS</div>
            <div className="text-2xl mt-1 font-medium">{full.journeys.filter(j => j.status !== 'completed').length}</div>
          </div>
        </div>

        {/* The critical uncertainties table — this is often more valuable than the visual */}
        <div className="mb-10">
          <div className="uppercase tracking-widest text-xs mb-2 text-bench-accent">Critical &amp; High Uncertainty Knots — Next Questions</div>
          <table className="w-full text-sm border">
            <thead>
              <tr className="border-b bg-slate-50 text-left">
                <th className="p-2 font-normal">Knot</th>
                <th className="p-2 font-normal">Uncertainty</th>
                <th className="p-2 font-normal">Known / Unknown (excerpt)</th>
                <th className="p-2 font-normal">Evidence needed</th>
                <th className="p-2 font-normal">Next question + owner</th>
              </tr>
            </thead>
            <tbody>
              {high.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-slate-500">No high or critical uncertainty knots at time of cast.</td></tr>}
              {high.map(n => (
                <tr key={n.id} className="border-b align-top">
                  <td className="p-2 font-medium">{n.label}</td>
                  <td className="p-2 text-xs uppercase">{n.uncertainty}</td>
                  <td className="p-2 text-xs text-slate-600">
                    {n.known && <div className="text-emerald-700">K: {n.known.slice(0, 110)}{n.known.length > 110 ? '…' : ''}</div>}
                    {n.unknown && <div className="text-amber-700 mt-0.5">U: {n.unknown.slice(0, 110)}{n.unknown.length > 110 ? '…' : ''}</div>}
                  </td>
                  <td className="p-2 text-xs">{n.evidence_need}</td>
                  <td className="p-2 text-xs">
                    {n.next_question}<br />
                    <span className="text-slate-400">→ {n.next_owner_hint || 'unassigned'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-xs text-slate-500 border-t pt-6">
          This is a snapshot Responsibility Map produced by Delegation Bench. The live workshop version can still be reopened and repaired. 
          The value is in the explicit capture of what was known, what was not, what evidence was still required, and what the next question was at the moment of casting.
        </div>

        <div className="mt-8 flex gap-3 no-print">
          <button onClick={handlePrint} className="px-4 py-2 border rounded">Print / Save PDF</button>
          <a href={`/workshop/${mapId}`} className="px-4 py-2 border rounded inline-flex items-center">Re-open in the Workshop</a>
          <Link to="/" className="px-4 py-2 text-sm self-center text-bench-accent">Start another map</Link>
        </div>
      </div>

      <div className="text-center py-8 text-xs text-slate-400 border-t no-print">
        Built by Abhay Pratap Singh • <a href="https://www.linkedin.com/in/mindful-abhay/" className="underline">LinkedIn</a> • <a href="https://buymeacoffee.com/abhaypratapsingh" className="underline">Buy me a coffee</a>
      </div>
    </div>
  )
}

// --- Landing ---
function Landing() {
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)

  const startNew = async () => {
    setCreating(true)
    try {
      const created = await createMap('New AI Delegation Repair Map')
      // Go straight into the workshop with the secret in the URL hash
      window.location.href = `/workshop/${created.id}#secret=${created.edit_secret}`
    } catch (e) {
      toast.error('Could not create map. Make sure the worker is running (npm run dev).')
      setCreating(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <div className="max-w-[780px] mx-auto px-5 pt-16 pb-12">
        <div className="uppercase tracking-[4px] text-xs text-bench-accent mb-2">A COGNITIVE REPAIR INSTRUMENT</div>
        <h1 className="text-5xl font-semibold tracking-tighter leading-none">The workshop bench<br />for AI delegation confusion.</h1>

        <p className="mt-5 text-lg text-slate-600 max-w-prose">
          Nonprofit teams hand work to AI constantly — reports, outreach, analysis, volunteer coordination.
          The confusion that follows is rarely malicious. It is almost always implicit.
        </p>
        <p className="mt-3 text-lg text-slate-600 max-w-prose">
          Delegation Bench is a calm, focused place to externalize the knots using concept mapping, then deliberately repair them by naming the uncertainty, the known/unknown split, the evidence still needed, and the next sharp question.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <button onClick={startNew} disabled={creating} className="px-6 py-3 rounded bg-bench-accent text-white text-lg disabled:opacity-70">
            {creating ? 'Opening bench…' : 'Open a new repair session'}
          </button>
          <button onClick={() => {
            const id = prompt('Enter map ID (from a previous link):')
            if (id) navigate(`/workshop/${id}`)
          }} className="px-5 py-3 rounded border">Load existing map</button>
        </div>

        <div className="mt-6 text-sm text-slate-500">
          No account. No data extraction. Export the full map + delete it forever at any time.
        </div>

        {/* Why this, for nonprofits */}
        <div className="mt-14 grid md:grid-cols-3 gap-5 text-sm">
          {[
            ['Concept mapping as the engine', 'Externalizing mental models of responsibility reveals gaps that text alone hides. Nodes carry epistemic status, not just tasks.'],
            ['Four signals that matter', 'Uncertainty label. Known/unknown split. Evidence need. Next question. These four turn vague discomfort into actionable repair.'],
            ['The artifact is the product', 'When you cast the map you get a clean Responsibility Map you can actually send to a colleague, board, or funder. The process itself creates the value.'],
          ].map(([title, body], i) => (
            <div key={i} className="border-l-2 border-bench-accent pl-3">
              <div className="font-medium mb-1">{title}</div>
              <div className="text-slate-600">{body}</div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-xs border-t pt-6 text-slate-500 flex flex-wrap gap-x-4 items-center">
          Built by Abhay Pratap Singh as part of a series of public-utility cognitive tools.
          <a href="https://www.linkedin.com/in/mindful-abhay/" target="_blank" rel="noreferrer" className="font-medium hover:text-bench-accent">LinkedIn</a>
          <a href="https://buymeacoffee.com/abhaypratapsingh" target="_blank" rel="noreferrer" className="font-medium hover:text-bench-accent">Buy me a coffee</a>
          <span className="text-slate-400">•</span>
          <span>Uses Cloudflare Pages, Workers, D1, KV, Queues, Workflows, and Analytics Engine.</span>
        </div>
      </div>
    </div>
  )
}

// --- Router root ---
export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/workshop/:mapId" element={<Workshop />} />
      <Route path="/map/:mapId" element={<CastView />} />
    </Routes>
  )
}
